/**
 * LiveRoomLauncher — citizen / admin "go live" experience, now powered by
 * Agora RTC instead of Jitsi.
 *
 * Inline button rendered on each alert card. Decides what action to surface
 * based on the current user's relationship to the alert:
 *
 *   • Alert creator + no room      → "Start live report"
 *   • Alert creator + active room  → "Resume live report"
 *   • Admin + active room          → "Join live"  (proximity check on click)
 *   • Anyone else                  → not rendered
 *
 * The video UI is rendered into a Dialog by `LiveRoomDialog` (separate
 * file). We keep them apart so we can lazy-load the Agora SDK only when
 * the user actually opens a live room — the SDK is ~ 300 kB gzipped and
 * we don't want to charge every dashboard visitor for that bundle.
 */
import { lazy, Suspense, useCallback, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Video, Loader2, Radio, Wallet } from "lucide-react";
import { Button } from "./ui/button";
import useWalletBalance, { refreshWalletBalance } from "../hooks/useWalletBalance";

const LiveRoomDialog = lazy(() => import("./LiveRoomDialog"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Reporter pays ₦30 to start Live Report; chip warns when balance can't
// cover more than ~2 minutes so the user tops up BEFORE hitting Start
// and eating a 402. Mirrors LIVE_PRICE_PER_MIN_NGN in routes/live.py.
const LIVE_PRICE_NGN = 30;
const LOW_FUNDS_THRESHOLD_NGN = 60;

function getLocation() {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Geolocation unavailable"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      (e) => reject(e),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  });
}

export default function LiveRoomLauncher({ alert, user }) {
  const { t } = useTranslation();
  const isCreator = alert.user_id === user?.user_id;
  const isAdmin = user && (user.role === "system_admin" || user.role === "govt_admin");
  const isResolved = alert.status === "resolved";
  const liveActive = !!alert.live_room_active;

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [joinInfo, setJoinInfo] = useState(null);
  // Fetch wallet balance for the creator so we can render a "funds
  // remaining" chip that nudges a top-up BEFORE they hit Start and get
  // hit with a 402. Admins never see it — they're never billed.
  const shouldShowFunds = isCreator && !isResolved && !liveActive;
  const { balance: walletBalance } = useWalletBalance(shouldShowFunds);
  const lowFunds =
    walletBalance != null && walletBalance < LOW_FUNDS_THRESHOLD_NGN;
  const cannotStart =
    walletBalance != null && walletBalance < LIVE_PRICE_NGN;

  const handleClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      // (1) Citizen wants to START — call the create endpoint first.
      if (isCreator && !liveActive) {
        await axios.post(`${API}/alerts/${alert.alert_id}/live-room`, {}, { withCredentials: true });
      }
      // (2) Everyone (creator or admin) goes through the join endpoint to
      //     get a fresh Agora token. Admins must include their location
      //     so the server can run the proximity check.
      const body = {};
      if (isAdmin && !isCreator) {
        try {
          const loc = await getLocation();
          body.latitude = loc.latitude;
          body.longitude = loc.longitude;
        } catch {
          toast.error(t("live_room.gps_required", { defaultValue: "Location required to join — please allow GPS access." }));
          setBusy(false);
          return;
        }
      }
      const res = await axios.post(`${API}/alerts/${alert.alert_id}/live-room/join`, body, { withCredentials: true });
      setJoinInfo(res.data);
      setOpen(true);
      // Successful start debited ₦30 — refresh the chip so it reflects
      // the post-debit balance immediately.
      if (isCreator && !liveActive) refreshWalletBalance();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("live_room.join_failed", { defaultValue: "Could not join live report" }));
      // 402 (insufficient balance) is the classic case where our chip
      // was stale — force-refresh so the user sees the up-to-date
      // amount alongside the toast.
      if (err?.response?.status === 402) refreshWalletBalance();
    } finally {
      setBusy(false);
    }
  }, [alert.alert_id, busy, isAdmin, isCreator, liveActive, t]);

  if (isResolved) return null;
  if (!isCreator && !(isAdmin && liveActive)) return null;

  const label = !liveActive
    ? t("live_room.start", { defaultValue: "Start live report" })
    : isCreator
      ? t("live_room.resume", { defaultValue: "Resume live report" })
      : t("live_room.join_btn", { defaultValue: "Join live" });

  const className = liveActive
    ? "bg-red-600 hover:bg-red-700 text-white"
    : "bg-gray-900 hover:bg-black text-white";

  return (
    <>
      <div className="inline-flex items-center gap-1.5 shrink-0" data-testid={`live-room-launcher-${alert.alert_id}`}>
        <Button
          size="sm"
          onClick={handleClick}
          disabled={busy}
          className={`${className} h-7 text-[11px] rounded-sm font-body shrink-0 px-2 inline-flex items-center gap-1`}
          data-testid={`live-room-${liveActive ? "join" : "start"}-${alert.alert_id}`}
          title={cannotStart ? t("live_room.top_up_needed", { defaultValue: `Top up ₦${LIVE_PRICE_NGN} to start` }) : label}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : liveActive ? <Radio className="w-3 h-3 animate-pulse" /> : <Video className="w-3 h-3" />}
          {label}
        </Button>
        {shouldShowFunds && walletBalance != null && lowFunds && (
          <span
            className={`inline-flex items-center gap-1 h-7 px-1.5 rounded-sm border font-body text-[10px] font-bold ${
              cannotStart
                ? "border-red-500/40 bg-red-500/10 text-red-300"
                : "border-amber-500/40 bg-amber-500/10 text-amber-300"
            }`}
            data-testid={`live-room-funds-chip-${alert.alert_id}`}
            title={cannotStart
              ? t("live_room.chip_top_up_tooltip", { defaultValue: `Top up ₦${LIVE_PRICE_NGN} to start a Live Report — you have ₦${walletBalance} in your wallet.` })
              : t("live_room.chip_low_funds_tooltip", { defaultValue: `Your Scan2Call wallet has ₦${walletBalance} — enough for about ${Math.floor(walletBalance / LIVE_PRICE_NGN)} minute(s) of Live Report.` })
            }
          >
            <Wallet className="w-3 h-3" />
            ₦{walletBalance}
          </span>
        )}
      </div>
      {open && joinInfo && (
        <Suspense fallback={null}>
          <LiveRoomDialog
            alert={alert}
            joinInfo={joinInfo}
            isAdmin={isAdmin}
            onClose={() => { setOpen(false); setJoinInfo(null); }}
          />
        </Suspense>
      )}
    </>
  );
}
